// ClimateSafe Climate Risk API Client - Following HoneyDo patterns
import { 
  APIResponse, 
  APIError,
  PropertyRiskParams,
  PropertyRiskResponse,
  ComparisonParams,
  PropertyComparisonResponse,
  GeocodingParams,
  GeocodingResponse,
  ProfessionalSearchParams,
  ProfessionalSearchResponse,
  SpatialSearchParams,
  SpatialSearchResponse,
  HistoricalEventsParams,
  HistoricalEventsResponse,
  ClimateProjectionsResponse,
  BulkAnalysisRequest,
  BulkAnalysisJob,
  ReportGenerationRequest,
  ReportGenerationResponse,
  UserProfileUpdate,
  SubscriptionUpgrade,
  APIKeyRequest,
  APIKey,
  WebhookConfig,
  TrendDataParams
} from '../types/api';

// Environment configuration - Tim-Combo compliant API endpoint
const API_BASE = process.env.REACT_APP_CLIMATESAFE_API_URL || 'https://65o1wcdzf3.execute-api.us-east-2.amazonaws.com/dev';

// ClimateSafe API endpoints - Tim-Combo compliant /tim/{domain}/{action} convention
export const CLIMATESAFE_API_ENDPOINTS = {
  // System endpoints (public)
  SYSTEM: {
    HEALTH: API_BASE + '/tim/system/health'
  },

  // Authentication endpoints - /tim/auth/*
  AUTH: {
    REGISTER: API_BASE + '/tim/auth/register',
    PROFILE: API_BASE + '/tim/auth/profile',
    USAGE: API_BASE + '/tim/auth/usage'
  },

  // Risk assessment endpoints - /tim/risk/*
  RISK: {
    ASSESS: API_BASE + '/tim/risk/assess',
    COMPARE: API_BASE + '/tim/risk/compare',
    BULK: API_BASE + '/tim/risk/bulk',
    GEOGRAPHIC: API_BASE + '/tim/risk/geographic'
  },

  // Subscription management endpoints - /tim/subscription/*
  SUBSCRIPTION: {
    CURRENT: API_BASE + '/tim/subscription/current',
    UPGRADE: API_BASE + '/tim/subscription/upgrade',
    USAGE: API_BASE + '/tim/subscription/usage',
    BILLING: API_BASE + '/tim/subscription/billing'
  },

  // Billing endpoints - /tim/billing/*
  BILLING: {
    CHECKOUT: API_BASE + '/tim/billing/checkout'
  },

  // Legacy endpoint aliases for backward compatibility
  // TODO: Remove after frontend migration complete
  HEALTH: API_BASE + '/tim/system/health',

  // Geographic endpoint (uses risk/geographic)
  GEO: {
    SPATIAL: API_BASE + '/tim/risk/geographic'
  }
};

// Subscription tier type definition
export type SubscriptionTier = 'free' | 'premium' | 'professional';

// Subscription tier configurations
export const SUBSCRIPTION_LIMITS = {
  free: {
    monthly_assessments: 3,
    api_calls_per_hour: 0,
    bulk_analysis: false,
    professional_reports: false,
    api_access: false,
    webhook_support: false
  },
  premium: {
    monthly_assessments: 100,
    api_calls_per_hour: 100,
    bulk_analysis: true,
    professional_reports: true,
    api_access: true,
    webhook_support: false
  },
  professional: {
    monthly_assessments: 1000,
    api_calls_per_hour: 1000,
    bulk_analysis: true,
    professional_reports: true,
    api_access: true,
    webhook_support: true
  }
};

// Enhanced API client following HoneyDo patterns
export const Make_ClimateSafe_API_Call = async <T>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
  body?: any,
  options: any = {}
): Promise<APIResponse<T>> => {
  try {
    // Get auth token from HoneyDo Cognito
    const token = localStorage.getItem('honeydo_token') || localStorage.getItem('climatesafe_token');
    
    // Debug logging (only in development)
    if (process.env.REACT_APP_ENABLE_DEBUG_LOGGING === 'true') {
      console.log(`[ClimateSafe API] Making ${method} request to: ${endpoint}`, {
        options,
        hasToken: !!token,
        hasBody: !!body
      });
    }

    // Build fetch options with proper headers
    const fetchOptions: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Version': process.env.REACT_APP_VERSION || '1.0.0',
        'X-Client-Type': 'climatesafe-climate-risk-app',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...(options.headers || {})
      }
    };

    // Add request body for POST/PUT requests
    if (['POST', 'PUT'].includes(method) && body !== undefined) {
      fetchOptions.body = JSON.stringify(body);
    }

    // Build URL with query parameters if provided
    let url = endpoint.startsWith('http') ? endpoint : API_BASE + endpoint;
    
    if (options.params) {
      const searchParams = new URLSearchParams();
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url = `${url}${url.includes('?') ? '&' : '?'}${queryString}`;
      }
    }

    // Make the fetch request
    const response = await fetch(url, fetchOptions);
    
    // Check response content type
    const contentType = response.headers.get('content-type');
    const isJson = contentType && contentType.includes('application/json');
    
    // Process JSON responses
    if (isJson) {
      try {
        const data = await response.json();
        
        // Debug successful responses
        if (process.env.REACT_APP_ENABLE_DEBUG_LOGGING === 'true' && data.success) {
          console.log(`[ClimateSafe API] Success response from ${endpoint}:`, {
            message: data.message,
            dataType: typeof data.data,
            hasRiskData: !!data.data?.risk_assessment,
            hasProperties: !!data.data?.properties
          });
        }
        
        return data as APIResponse<T>;
      } catch (jsonError) {
        console.error('[ClimateSafe API] JSON parse error:', jsonError);
        
        return {
          success: false,
          error: {
            code: 'JSON_PARSE_ERROR',
            message: 'Server returned invalid JSON',
            details: `Status ${response.status} ${response.statusText} for ${url}`
          }
        };
      }
    }
    
    // Handle non-JSON responses (usually errors)
    try {
      const textContent = await response.text();
      const preview = textContent.substring(0, 200) + (textContent.length > 200 ? '...' : '');
      console.error(`[ClimateSafe API] Non-JSON response: ${contentType} for ${url}, preview: ${preview}`);
      
      return {
        success: false,
        error: {
          code: 'NON_JSON_RESPONSE',
          message: 'Server response was not in JSON format',
          details: `Content-Type: ${contentType || 'unknown'}, Status: ${response.status}`
        }
      };
    } catch (e) {
      console.error('[ClimateSafe API] Could not read response:', e);
      
      return {
        success: false,
        error: {
          code: 'RESPONSE_READ_ERROR',
          message: 'Unable to read server response',
          details: `Status ${response.status} ${response.statusText} for ${url}`
        }
      };
    }
  } catch (error) {
    // Handle network errors and other exceptions
    console.error('[ClimateSafe API] Request failed:', error);
    
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: error instanceof Error ? error.message : 'Network request failed',
        details: JSON.stringify(error)
      }
    };
  }
};

// Helper function to make authorized API calls with subscription tier validation
export const MakeAuthorizedCall = async <T>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
  body?: any,
  options: any = {},
  requiredTier?: SubscriptionTier
): Promise<APIResponse<T>> => {
  // Check subscription tier if required
  if (requiredTier) {
    const userTier = localStorage.getItem('climatesafe_user_tier') as SubscriptionTier;
    if (!userTier || !isSubscriptionTierSufficient(userTier, requiredTier)) {
      return {
        success: false,
        error: {
          code: 'INSUFFICIENT_SUBSCRIPTION',
          message: `Feature requires ${requiredTier} tier, current tier: ${userTier || 'none'}`,
          details: 'Please upgrade your subscription to access this feature'
        }
      };
    }
  }

  return Make_ClimateSafe_API_Call<T>(endpoint, method, body, options);
};

// Helper function to check if subscription tier is sufficient
function isSubscriptionTierSufficient(currentTier: SubscriptionTier, requiredTier: SubscriptionTier): boolean {
  const tierHierarchy: Record<SubscriptionTier, number> = {
    free: 0,
    premium: 1,
    professional: 2
  };
  
  return tierHierarchy[currentTier] >= tierHierarchy[requiredTier];
}

// Convenience wrapper for the ClimateSafeApiClient class
// Updated for Tim-Combo compliant /tim/ routes
export class ClimateSafeApiClient {
  // Test API connection using /tim/system/health
  async testConnection(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await Make_ClimateSafe_API_Call(CLIMATESAFE_API_ENDPOINTS.SYSTEM.HEALTH);
      if (response.success) {
        return { success: true, message: 'ClimateSafe API connection successful' };
      } else {
        return { success: false, message: response.error?.message || 'API connection failed' };
      }
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'API connection test failed'
      };
    }
  }

  // Property Risk Assessment Methods - /tim/risk/*
  async assessPropertyRisk(params: PropertyRiskParams): Promise<PropertyRiskResponse> {
    // GET /tim/risk/assess with address as query param
    return MakeAuthorizedCall(CLIMATESAFE_API_ENDPOINTS.RISK.ASSESS, 'GET', undefined, { params });
  }

  async compareProperties(params: ComparisonParams): Promise<PropertyComparisonResponse> {
    // POST /tim/risk/compare
    return MakeAuthorizedCall(CLIMATESAFE_API_ENDPOINTS.RISK.COMPARE, 'POST', params, {}, 'premium');
  }

  async geographicSearch(params: SpatialSearchParams): Promise<SpatialSearchResponse> {
    // GET /tim/risk/geographic
    return MakeAuthorizedCall(CLIMATESAFE_API_ENDPOINTS.RISK.GEOGRAPHIC, 'GET', undefined, { params }, 'premium');
  }

  // Legacy alias for backward compatibility
  async spatialSearch(params: SpatialSearchParams): Promise<SpatialSearchResponse> {
    return this.geographicSearch(params);
  }

  // Bulk Analysis Methods (Professional) - POST /tim/risk/bulk
  async createBulkAnalysis(request: BulkAnalysisRequest): Promise<APIResponse<BulkAnalysisJob>> {
    return MakeAuthorizedCall(CLIMATESAFE_API_ENDPOINTS.RISK.BULK, 'POST', request, {}, 'professional');
  }

  // User Profile and Auth Methods - /tim/auth/*
  async getUserProfile(): Promise<APIResponse<any>> {
    // GET /tim/auth/profile
    return MakeAuthorizedCall(CLIMATESAFE_API_ENDPOINTS.AUTH.PROFILE, 'GET');
  }

  async updateUserProfile(updates: UserProfileUpdate): Promise<APIResponse<any>> {
    // PUT /tim/auth/profile
    return MakeAuthorizedCall(CLIMATESAFE_API_ENDPOINTS.AUTH.PROFILE, 'PUT', updates);
  }

  async getUserUsage(period?: string): Promise<APIResponse<any>> {
    // GET /tim/auth/usage
    return MakeAuthorizedCall(CLIMATESAFE_API_ENDPOINTS.AUTH.USAGE, 'GET', undefined, {
      params: period ? { period } : undefined
    });
  }

  // Subscription Methods - /tim/subscription/*
  async getCurrentSubscription(): Promise<APIResponse<any>> {
    // GET /tim/subscription/current
    return MakeAuthorizedCall(CLIMATESAFE_API_ENDPOINTS.SUBSCRIPTION.CURRENT, 'GET');
  }

  async upgradeSubscription(upgrade: SubscriptionUpgrade): Promise<APIResponse<any>> {
    // POST /tim/subscription/upgrade
    return MakeAuthorizedCall(CLIMATESAFE_API_ENDPOINTS.SUBSCRIPTION.UPGRADE, 'POST', upgrade);
  }

  async getSubscriptionUsage(): Promise<APIResponse<any>> {
    // GET /tim/subscription/usage
    return MakeAuthorizedCall(CLIMATESAFE_API_ENDPOINTS.SUBSCRIPTION.USAGE, 'GET');
  }

  async getBillingPortal(): Promise<APIResponse<any>> {
    // GET /tim/subscription/billing
    return MakeAuthorizedCall(CLIMATESAFE_API_ENDPOINTS.SUBSCRIPTION.BILLING, 'GET');
  }

  // Legacy alias for backward compatibility
  async getUsageStats(): Promise<APIResponse<any>> {
    return this.getSubscriptionUsage();
  }

  // Billing Methods - /tim/billing/*
  async createCheckoutSession(tier: string, billingCycle: string = 'monthly', conversionTrigger?: string): Promise<APIResponse<any>> {
    // POST /tim/billing/checkout
    return MakeAuthorizedCall(CLIMATESAFE_API_ENDPOINTS.BILLING.CHECKOUT, 'POST', {
      tier,
      billing_cycle: billingCycle,
      conversion_trigger: conversionTrigger || 'manual'
    });
  }

  // Usage tracking for free tier limits
  async checkUsageLimits(): Promise<{ canMakeRequest: boolean; remaining: number; resetDate: Date }> {
    try {
      const userTier = localStorage.getItem('climatesafe_user_tier') as SubscriptionTier || 'free';
      const response = await this.getSubscriptionUsage();

      if (!response.success) {
        // If usage check fails, allow request but warn
        console.warn('Could not check usage limits, allowing request');
        return { canMakeRequest: true, remaining: 0, resetDate: new Date() };
      }

      const limits = SUBSCRIPTION_LIMITS[userTier];
      const usage = response.data?.usage || response.data;

      const searchesUsed = usage?.limits?.risk_assessments?.used || 0;
      const searchLimit = limits.monthly_assessments;

      return {
        canMakeRequest: searchesUsed < searchLimit,
        remaining: Math.max(0, searchLimit - searchesUsed),
        resetDate: new Date(usage?.billing_period?.end || Date.now())
      };
    } catch (error) {
      console.error('Error checking usage limits:', error);
      return { canMakeRequest: true, remaining: 0, resetDate: new Date() };
    }
  }
}

// Export singleton instance for backward compatibility
export const climatesafeApiClient = new ClimateSafeApiClient();

// Helper function to check if user has reached free tier limit
export async function checkFreeTierLimits(): Promise<{
  hasReachedLimit: boolean;
  usageInfo: {
    used: number;
    limit: number;
    remaining: number;
    resetDate: Date;
  };
}> {
  const userTier = localStorage.getItem('climatesafe_user_tier') as SubscriptionTier || 'free';
  
  if (userTier !== 'free') {
    return {
      hasReachedLimit: false,
      usageInfo: {
        used: 0,
        limit: SUBSCRIPTION_LIMITS[userTier].monthly_assessments,
        remaining: SUBSCRIPTION_LIMITS[userTier].monthly_assessments,
        resetDate: new Date()
      }
    };
  }

  const usageCheck = await climatesafeApiClient.checkUsageLimits();
  const limits = SUBSCRIPTION_LIMITS.free;
  
  return {
    hasReachedLimit: !usageCheck.canMakeRequest,
    usageInfo: {
      used: limits.monthly_assessments - usageCheck.remaining,
      limit: limits.monthly_assessments,
      remaining: usageCheck.remaining,
      resetDate: usageCheck.resetDate
    }
  };
}